import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Check, X, Coins, ShieldAlert, ArrowLeft, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useSubscription } from "@/hooks/useSubscription";
import { useTestClock } from "@/contexts/TestClockContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

const tiers = [
  {
    name: "Starter",
    tier: "starter",
    price: { monthly: "€5", annual: "€50" },
    credits: 200,
    description: "For professionals who need basic email extraction",
    features: [
      { name: "200 AI credits/month", included: true },
      { name: "Email extraction (from 1 credit)", included: true },
      { name: "Basic dashboard & CSV export", included: true },
      { name: "Community support", included: true },
      { name: "Smart Categories", included: false },
      { name: "Smart Notes", included: false },
      { name: "Field Presets", included: false },
      { name: "API access", included: false },
    ],
    popular: false,
  },
  {
    name: "Pro",
    tier: "pro",
    price: { monthly: "€19", annual: "€190" },
    credits: 1000,
    description: "The complete AI assistant for power users",
    features: [
      { name: "1,000 AI credits/month", included: true },
      { name: "Email extraction", included: true },
      { name: "Smart Categories (up to 10)", included: true },
      { name: "Smart Notes (up to 10 tasks)", included: true },
      { name: "Field Presets (unlimited)", included: true },
      { name: "Priority support (24h SLA)", included: true },
      { name: "API access & Zapier", included: true },
      { name: "Advanced analytics", included: true },
    ],
    popular: true,
  },
  {
    name: "Enterprise",
    tier: "enterprise",
    price: { monthly: "€99+", annual: "Custom" },
    credits: 5000,
    description: "For teams who need scale and customization",
    features: [
      { name: "5,000+ AI credits/month", included: true },
      { name: "All Pro features", included: true },
      { name: "Unlimited Smart Categories & Notes", included: true },
      { name: "Batch processing & CRM sync", included: true },
      { name: "Dedicated account manager", included: true },
      { name: "Custom integrations", included: true },
      { name: "SLA-guaranteed support", included: true },
      { name: "Privacy-focused EU processing", included: true },
    ],
    popular: false,
  },
];

const topupPackages = [
  { name: "Mini", type: "mini", credits: 100, price: 5 },
  { name: "Standard", type: "standard", credits: 500, price: 18, discount: "10% off" },
  { name: "Power", type: "power", credits: 2000, price: 55, discount: "30% off" },
  { name: "Agency", type: "agency", credits: 10000, price: 220, discount: "45% off" },
];

export default function Plans() {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");
  const [loading, setLoading] = useState<string | null>(null);
  const { subscription, isLoading: subLoading } = useSubscription();
  const { openCheckoutInNewTab } = useTestClock();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubscribe = async (tier: string) => {
    if (tier === "enterprise") {
      navigate("/support#contact");
      return;
    }

    setLoading(`${tier}_${billingCycle}`);
    
    // Check if user has an active subscription (or cancelled but still running)
    const hasActiveSubscription = subscription !== null && 
      (subscription?.status === 'active' || 
       (subscription?.status === 'cancelled' && subscription?.current_period_end && new Date(subscription.current_period_end) > new Date()));
    
    // Check if this is a plan change (user already has active subscription to different tier)
    const isCurrentTier = subscription?.tier === tier;
    const isActivePlanChange = hasActiveSubscription && !isCurrentTier;

    try {
      const { data, error } = await supabase.functions.invoke('create-subscription-checkout', {
        body: { tier, billing_cycle: billingCycle },
      });

      if (error) throw error;
      
      // Check if trying to subscribe to same plan
      if (data?.error) {
        toast({
          title: "Subscription Already Active",
          description: data.error,
          variant: "destructive",
        });
        return;
      }
      
      // Check if it's a billing portal redirect (for normal users changing plans)
      if (data?.is_billing_portal) {
        toast({
          title: "Opening Customer Portal",
          description: "You can change your plan in the next window",
        });
        // Open in new tab for better UX
        if (data?.url) {
          window.open(data.url, '_blank');
          return; // Don't redirect in same window
        }
      }

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast({
        title: "Error",
        description: "Failed to create checkout session",
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  const handleBuyTopup = async (packageType: string) => {
    setLoading(packageType);
    try {
      const { data, error } = await supabase.functions.invoke('create-topup-payment', {
        body: { packageType },
      });

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Topup error:', error);
      toast({
        title: "Error",
        description: "Failed to create payment session",
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  const hasActiveSubscription = subscription !== null && 
    (subscription?.status === 'active' || 
     (subscription?.status === 'cancelled' && subscription?.current_period_end && new Date(subscription.current_period_end) > new Date()));

  // Show loading state while subscription is being fetched
  if (subLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow bg-background">
          <section className="container px-4 py-16 md:py-24">
            <div className="text-center">
              <p className="text-muted-foreground">Loading your subscription...</p>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow bg-background">
        <section className="container px-4 py-16 md:py-24">
          {/* Back to Settings Button */}
          <div className="mb-8">
            <Button asChild variant="outline">
              <Link to="/settings" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Settings
              </Link>
            </Button>
          </div>

          {/* Reactivation Banner for Cancelled Subscriptions */}
          {subscription?.status === 'cancelled' && (
            <Alert className="mb-8 border-blue-500 bg-blue-50 dark:bg-blue-950">
              <AlertCircle className="h-5 w-5 text-blue-600" />
              <AlertDescription>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold text-blue-900 dark:text-blue-200 mb-1">
                      {new Date(subscription.current_period_end) > new Date() 
                        ? "Welcome Back! 👋" 
                        : "We missed you! 🎉"}
                    </p>
                    <p className="text-blue-800 dark:text-blue-300 text-sm">
                      {new Date(subscription.current_period_end) > new Date() 
                        ? `Your current plan ends on ${format(new Date(subscription.current_period_end), 'PPP')}. Choose a new subscription below to continue without interruption.`
                        : "Your subscription has ended. Select a plan below to regain access to all premium features."}
                    </p>
                  </div>
                  <Button 
                    size="sm" 
                    className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 whitespace-nowrap"
                    onClick={() => {
                      document.getElementById('subscription-tiers')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    See Plans Below ↓
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="mb-4" variant="secondary">
              Transparent Pricing
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Choose Your Plan
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Credit-based system that scales with your workflow. No hidden fees. Cancel anytime.
            </p>
            
            {/* Billing Toggle */}
            <div className="inline-flex items-center gap-3 bg-muted p-1 rounded-lg">
              <button
                onClick={() => setBillingCycle("monthly")}
                className={`px-4 py-2 rounded-md transition-colors ${
                  billingCycle === "monthly"
                    ? "bg-background shadow-sm font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle("annual")}
                className={`px-4 py-2 rounded-md transition-colors ${
                  billingCycle === "annual"
                    ? "bg-background shadow-sm font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Annual
                <Badge className="ml-2" variant="secondary">
                  Save 2 months
                </Badge>
              </button>
            </div>
          </div>

          {/* Subscription Tiers */}
          <div id="subscription-tiers" className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto mb-16">
            {tiers.map((tier) => {
              const isCurrentTier = subscription?.tier === tier.tier;
              const isCurrentCycle = subscription?.billing_cycle === billingCycle;
              const isCurrentPlan = isCurrentTier && isCurrentCycle;
              const isCycleChange = isCurrentTier && !isCurrentCycle;
              
              // Determine upgrade/downgrade
              const tierOrder = { starter: 1, pro: 2, enterprise: 3 };
              const currentTierLevel = subscription ? tierOrder[subscription.tier as keyof typeof tierOrder] : 0;
              const thisTierLevel = tierOrder[tier.tier as keyof typeof tierOrder];
              const isUpgrade = thisTierLevel > currentTierLevel;
              const isDowngrade = thisTierLevel < currentTierLevel;
              
              let buttonText = `Subscribe to ${tier.name}`;
              let buttonVariant: "default" | "outline" = tier.popular ? "default" : "outline";
              
              if (isCurrentPlan) {
                buttonText = "Current Plan";
              } else if (isCycleChange) {
                buttonText = `Switch to ${billingCycle === 'monthly' ? 'Monthly' : 'Annual'}`;
                buttonVariant = "outline";
              } else if (isUpgrade) {
                buttonText = `Upgrade to ${tier.name}`;
                buttonVariant = "default";
              } else if (isDowngrade) {
                buttonText = `Downgrade to ${tier.name}`;
                buttonVariant = "outline";
              } else if (tier.name === "Enterprise") {
                buttonText = "Contact Sales";
              }
              
              return (
                <Card
                  key={tier.name}
                  className={`${tier.popular ? "border-primary shadow-elevated" : ""} ${isCurrentPlan ? "border-green-500 border-2" : ""} relative`}
                >
                  {tier.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <Badge className="bg-primary text-primary-foreground">Most Popular</Badge>
                    </div>
                  )}
                  {isCurrentPlan && (
                    <div className="absolute -top-4 right-4">
                      <Badge className="bg-green-500 text-white">Your Plan</Badge>
                    </div>
                  )}
                  {isCycleChange && (
                    <div className="absolute -top-4 right-4">
                      <Badge className="bg-blue-500 text-white">
                        Switch to {billingCycle === 'monthly' ? 'Monthly' : 'Annual'}
                      </Badge>
                    </div>
                  )}
                  {isUpgrade && !isCurrentTier && (
                    <div className="absolute -top-4 right-4">
                      <Badge className="bg-blue-500 text-white">↑ Upgrade</Badge>
                    </div>
                  )}
                  {isDowngrade && (
                    <div className="absolute -top-4 right-4">
                      <Badge className="bg-orange-500 text-white">↓ Downgrade</Badge>
                    </div>
                  )}
                  <CardHeader className="text-center pb-4">
                    <CardTitle className="text-2xl mb-2">{tier.name}</CardTitle>
                    <div className="mb-2">
                      <span className="text-4xl font-bold">
                        {tier.price[billingCycle]}
                      </span>
                      {tier.name !== "Enterprise" && (
                        <>
                          <span className="text-muted-foreground">
                            /{billingCycle === "monthly" ? "month" : "year"}
                          </span>
                          <p className="text-xs text-muted-foreground mt-1">
                            plus 19% VAT
                          </p>
                        </>
                      )}
                    </div>
                    <CardDescription>{tier.description}</CardDescription>
                    <div className="flex items-center justify-center gap-2 mt-2">
                      <Coins className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium">
                        {tier.credits.toLocaleString()} credits/month
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Separator />
                    <ul className="space-y-3">
                      {tier.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          {feature.included ? (
                            <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                          ) : (
                            <X className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                          )}
                          <span
                            className={
                              feature.included
                                ? "text-foreground"
                                : "text-muted-foreground line-through"
                            }
                          >
                            {feature.name}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      className="w-full"
                      variant={buttonVariant}
                      onClick={() => handleSubscribe(tier.tier)}
                      disabled={loading === `${tier.tier}_${billingCycle}` || isCurrentPlan}
                    >
                      {loading === `${tier.tier}_${billingCycle}` ? (
                        "Opening..."
                      ) : (
                        buttonText
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Top-ups Section - Only for active subscribers */}
          {hasActiveSubscription && (
            <>
              <Separator className="my-16 max-w-7xl mx-auto" />
              
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold mb-4">Need More Credits?</h2>
                  <p className="text-muted-foreground">
                    Top up your account with additional credits anytime
                  </p>
                </div>

                <div className="grid md:grid-cols-4 gap-6">
                  {topupPackages.map((pkg) => (
                    <Card key={pkg.name} className="relative">
                      {pkg.discount && (
                        <div className="absolute -top-3 right-4">
                          <Badge variant="secondary">{pkg.discount}</Badge>
                        </div>
                      )}
                      <CardHeader className="text-center pb-4">
                        <CardTitle className="text-xl mb-2">{pkg.name}</CardTitle>
                        <div className="mb-2">
                          <span className="text-3xl font-bold">€{pkg.price}</span>
                          <p className="text-xs text-muted-foreground mt-1">
                            plus 19% VAT
                          </p>
                        </div>
                        <div className="flex items-center justify-center gap-2">
                          <Coins className="w-4 h-4 text-primary" />
                          <span className="text-sm font-medium">
                            {pkg.credits.toLocaleString()} credits
                          </span>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <Button
                          className="w-full"
                          variant="outline"
                          onClick={() => handleBuyTopup(pkg.type)}
                          disabled={loading === pkg.type}
                        >
                          {loading === pkg.type ? "Opening checkout..." : "Buy Credits"}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* FAQ Preview */}
          <div className="mt-24 max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4">Have questions?</h2>
            <p className="text-muted-foreground mb-6">
              Check out our FAQ or contact support for help choosing the right plan.
            </p>
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => navigate("/support#faq")}>
                View FAQ
              </Button>
              <Button onClick={() => navigate("/support")}>
                Contact Support
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
